<template>
  <slot :key="retryKey" />
</template>

<script setup lang="ts">
import { ref, onErrorCaptured } from '@/composition/vue/compositionApi'
import { TmServiceNotBoundError } from '@/core/error/tmServiceNotBoundError'
import { priorityLoadAndBindAllDepsForModules } from '@/core/container/deps'
import type { AppModule } from '@/config/types'
import { getMonitoringLoggerService } from '@/core/container/ioc'
import { LogLevel } from '@/core/logger/types'
import { TmRuntimeError } from '@/core/error/tmRuntimeError'

const retryKey = ref(0)
const pendingModules = new Set<AppModule>()
let loadingPromise: Promise<void> | null = null

onErrorCaptured((err) => {
  if (!(err instanceof TmServiceNotBoundError)) {
    return
  }

  pendingModules.add(err.module)
  scheduleLoad()

  return false
})

function scheduleLoad() {
  if (loadingPromise) {
    return
  }

  // Defer to microtask to batch all synchronous errors from current render
  loadingPromise = new Promise<void>((resolve) => {
    queueMicrotask(() => {
      const modules = Array.from(pendingModules)
      pendingModules.clear()

      // eslint-disable-next-line no-console
      console.warn(`[ModuleErrorBoundary] Loading missing modules: ${modules.join(', ')}…`)

      priorityLoadAndBindAllDepsForModules('', modules)
        .then(() => {
          retryKey.value += 1
        })
        .catch((e) => {
          const errorToThrow = e instanceof Error ? e : new TmRuntimeError(String(e))
          getMonitoringLoggerService().logAndCapture(errorToThrow, LogLevel.ERROR)
        })
        .finally(() => {
          loadingPromise = null
          resolve()
        })
    })
  })
}
</script>
